import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertTriangle, Search, Plus, ChevronRight, 
  Shield, Link, Calendar, X, CheckCircle, Lock
} from 'lucide-react';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { getPermissions } from '../../utils/permissions';
import CommentsSection from './CommentsSection';
import { PresenceAwareness, PresenceBadge } from './PresenceIndicator';

const CreateRiskModal = ({ isOpen, onClose, onCreate }) => {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [category, setCategory] = useState('Operational');
  const [description, setDescription] = useState('');
  const [owner, setOwner] = useState('');

  if (!isOpen) return null;

  const displayOwner = owner || user?.name || '';

  const handleSubmit = () => {
    if (!title.trim()) return;
    const finalOwner = displayOwner.trim() || user?.name || 'Unassigned';
    onCreate({
      title: title.trim(),
      severity,
      owner: finalOwner,
      category,
      description: description.trim() || `Risk logged manually: ${title.trim()}`,
      mitigation: 'Mitigation plan to be developed.',
    });
    setTitle('');
    setSeverity('medium');
    setDescription('');
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-md rounded-2xl bg-[#0d1221] border border-white/10 shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="create-risk-modal"
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-white">Log New Risk</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Risk Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Describe the risk..."
              className="w-full h-10 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
              autoFocus
              data-testid="create-risk-title-input"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Severity</label>
            <div className="flex gap-2">
              {['low', 'medium', 'high'].map((s) => (
                <button
                  key={s}
                  onClick={() => setSeverity(s)}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium capitalize transition-colors ${
                    severity === s 
                      ? s === 'high' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                        s === 'medium' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                        'bg-green-500/20 text-green-400 border border-green-500/30'
                      : 'bg-white/5 text-gray-400 border border-white/10'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full h-10 px-4 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-blue-500/50 appearance-none cursor-pointer"
            >
              <option value="Financial">Financial</option>
              <option value="Compliance">Compliance</option>
              <option value="Security">Security</option>
              <option value="Resource">Resource</option>
              <option value="Operational">Operational</option>
              <option value="Technical">Technical</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Owner</label>
            <input
              type="text"
              value={displayOwner}
              onChange={(e) => setOwner(e.target.value)}
              placeholder={user?.name || 'Enter owner name...'}
              className="w-full h-10 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
              data-testid="create-risk-owner-input"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the risk in detail..."
              rows={3}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 resize-none"
              data-testid="create-risk-description-input"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button 
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            disabled={!title.trim()}
            className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="create-risk-submit-btn"
          >
            Log Risk
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const RiskComplianceScreen = () => {
  const { risks, tasks, createRisk, updateRisk } = useData();
  const { user, token } = useAuth();
  const perms = getPermissions(user?.role);
  const [selectedRiskId, setSelectedRiskId] = useState(risks[0]?.id);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const selectedRisk = risks.find(r => r.id === selectedRiskId) || risks[0];

  const getLinkedTasks = (risk) => {
    if (!risk) return [];
    if (risk.meetingId) {
      return tasks.filter(t => t.meetingId === risk.meetingId).slice(0, 3);
    }
    return [];
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500 text-red-500';
      case 'high': return 'bg-orange-500 text-orange-500';
      case 'medium': return 'bg-yellow-500 text-yellow-500';
      case 'low': return 'bg-green-500 text-green-500';
      default: return 'bg-gray-500 text-gray-500';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-red-500/20 text-red-400';
      case 'mitigating': return 'bg-orange-500/20 text-orange-400';
      case 'monitoring': return 'bg-blue-500/20 text-blue-400';
      case 'resolved': return 'bg-green-500/20 text-green-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const filteredRisks = risks.filter(risk => {
    if (filterStatus !== 'all' && risk.status !== filterStatus) return false;
    if (searchQuery && !risk.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const linkedTasks = getLinkedTasks(selectedRisk);

  const handleStatusChange = (riskId, newStatus) => {
    updateRisk(riskId, { status: newStatus });
  };

  const nextStatusMap = {
    'open': 'mitigating',
    'mitigating': 'monitoring',
    'monitoring': 'resolved',
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex gap-6" data-testid="risk-compliance-screen">
      {/* Risk Table */}
      <div className="flex-1 flex flex-col rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-white">Risk & Compliance</h1>
              <p className="text-sm text-gray-400 mt-1">Track and manage organizational risks</p>
            </div>
            {perms.canCreateRisk ? (
              <button 
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium hover:opacity-90 transition-opacity"
                data-testid="log-risk-btn"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm">Log Risk</span>
              </button>
            ) : (
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 text-gray-500 cursor-not-allowed" title="Insufficient permissions" data-testid="log-risk-disabled">
                <Lock className="w-4 h-4" />
                <span className="text-sm">Log Risk</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search risks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-10 pr-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
                data-testid="risk-search-input"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="h-10 px-4 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-blue-500/50 appearance-none cursor-pointer"
              data-testid="risk-filter-status"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="mitigating">Mitigating</option>
              <option value="monitoring">Monitoring</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          <table className="w-full">
            <thead className="bg-white/5 sticky top-0">
              <tr className="text-left text-sm text-gray-400">
                <th className="px-4 py-3 font-medium">Risk</th>
                <th className="px-4 py-3 font-medium">Severity</th>
                <th className="px-4 py-3 font-medium">Owner</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {filteredRisks.map((risk) => (
                <tr
                  key={risk.id}
                  onClick={() => setSelectedRiskId(risk.id)}
                  className={`border-b border-white/5 cursor-pointer transition-colors ${
                    selectedRisk?.id === risk.id ? 'bg-blue-500/10' : 'hover:bg-white/5'
                  }`}
                  data-testid={`risk-row-${risk.id}`}
                >
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className={`w-4 h-4 ${getSeverityColor(risk.severity).split(' ')[1]}`} />
                      <span className="text-white font-medium">{risk.title}</span>
                      <PresenceBadge itemType="risk" itemId={risk.id} />
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${
                      risk.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                      risk.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                      risk.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-green-500/20 text-green-400'
                    }`}>
                      {risk.severity}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-gray-400">{risk.owner}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(risk.status)}`}>
                      {risk.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    {risk.meetingId ? (
                      <span className="px-2 py-1 rounded-md bg-blue-500/10 text-blue-400 text-xs">
                        Auto-detected
                      </span>
                    ) : (
                      <span className="text-gray-500 text-sm">Manual</span>
                    )}
                  </td>
                </tr>
              ))}
              {filteredRisks.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No risks found matching your filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Panel */}
      {selectedRisk && (
        <div className="w-96 flex-shrink-0 flex flex-col rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 overflow-hidden" data-testid="risk-detail-panel">
          <div className="p-5 border-b border-white/10">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-white">Risk Details</h2>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(selectedRisk.status)}`}>
                {selectedRisk.status}
              </span>
            </div>
            <h3 className="text-white font-medium mb-2">{selectedRisk.title}</h3>
            <PresenceAwareness itemType="risk" itemId={selectedRisk.id} />
            {selectedRisk.meetingId && (
              <span className="px-2 py-1 rounded-md bg-orange-500/10 text-orange-400 text-xs">
                Auto-detected from Meeting
              </span>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {/* Metadata */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-xl bg-white/5">
                <p className="text-xs text-gray-400 mb-1">Severity</p>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${
                  selectedRisk.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                  selectedRisk.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                  selectedRisk.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-green-500/20 text-green-400'
                }`}>
                  {selectedRisk.severity}
                </span>
              </div>
              <div className="p-3 rounded-xl bg-white/5">
                <p className="text-xs text-gray-400 mb-1">Category</p>
                <p className="text-white text-sm">{selectedRisk.category}</p>
              </div>
              <div className="p-3 rounded-xl bg-white/5">
                <p className="text-xs text-gray-400 mb-1">Owner</p>
                <p className="text-white text-sm">{selectedRisk.owner}</p>
              </div>
              <div className="p-3 rounded-xl bg-white/5">
                <p className="text-xs text-gray-400 mb-1">Created</p>
                <p className="text-white text-sm">{new Date(selectedRisk.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
              </div>
            </div>

            {/* Description */}
            <div>
              <h4 className="text-sm font-medium text-gray-400 mb-2">Description</h4>
              <p className="text-gray-300 text-sm leading-relaxed">{selectedRisk.description}</p>
            </div>

            {/* Mitigation */}
            <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-blue-400" />
                <h4 className="text-sm font-medium text-blue-400">Mitigation Plan</h4>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">{selectedRisk.mitigation}</p>
            </div>

            {/* Status Action */}
            {perms.canEditRisk && selectedRisk.status !== 'resolved' && nextStatusMap[selectedRisk.status] && (
              <button 
                onClick={() => handleStatusChange(selectedRisk.id, nextStatusMap[selectedRisk.status])}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-500/20 text-blue-400 text-sm font-medium hover:bg-blue-500/30 transition-colors"
                data-testid="risk-advance-status-btn"
              >
                <CheckCircle className="w-4 h-4" />
                Move to {nextStatusMap[selectedRisk.status]}
              </button>
            )}
            {perms.canEditRisk && selectedRisk.status !== 'resolved' && (
              <button 
                onClick={() => handleStatusChange(selectedRisk.id, 'resolved')}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-green-500/20 text-green-400 text-sm font-medium hover:bg-green-500/30 transition-colors"
                data-testid="risk-resolve-btn"
              >
                <CheckCircle className="w-4 h-4" />
                Resolve Risk
              </button>
            )}
            {!perms.canEditRisk && selectedRisk.status !== 'resolved' && (
              <div className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/5 text-gray-500 text-sm cursor-not-allowed" title="Insufficient permissions">
                <Lock className="w-4 h-4" />
                Status changes restricted
              </div>
            )}

            {/* Linked Tasks */}
            {linkedTasks.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Link className="w-4 h-4 text-gray-400" />
                  <h4 className="text-sm font-medium text-gray-400">Linked Tasks</h4>
                </div>
                <div className="space-y-2">
                  {linkedTasks.map((task) => (
                    <div key={task.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
                      <CheckCircle className={`w-4 h-4 ${task.column === 'completed' ? 'text-green-400' : 'text-gray-400'}`} />
                      <span className="text-sm text-white flex-1 truncate">{task.title}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        task.column === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                      }`}>{task.column}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <CommentsSection itemType="risk" itemId={selectedRisk.id} token={token} />
          </div>
        </div>
      )}

      {/* Create Risk Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateRiskModal 
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onCreate={createRisk}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
